import React, { useState, useMemo } from 'react';
import { Button } from '../../../ui';
import { faLocationCrosshairs, faSearch } from '@fortawesome/free-solid-svg-icons';
import {
    useShopRecommendations,
    useCoffeeTypes,
    TransportMode
} from '../hooks/useCoffeeShops';
import { ShopCard, TransportPicker, CoffeeBadge } from '../components';

interface ShopRecommendationsViewProps {
    onShopClick?: (shopId: string) => void;
}

const ShopRecommendationsView: React.FC<ShopRecommendationsViewProps> = ({ onShopClick }) => {
    const { coffeeTypes, loading: loadingTypes } = useCoffeeTypes();
    const { recommendations, loading, error, fetchRecommendations } = useShopRecommendations();

    const [selectedCoffeeType, setSelectedCoffeeType] = useState<string>('');
    const [transport, setTransport] = useState<TransportMode>('foot');
    const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
    const [locating, setLocating] = useState(false);
    const [locationError, setLocationError] = useState<string | null>(null);

    // build coffee types cache for ShopCard
    const coffeeTypesCache = useMemo(() => {
        const cache: Record<string, { iconName: string; displayName: string }> = {};
        coffeeTypes.forEach(ct => {
            cache[ct.coffeeKey] = { iconName: ct.iconName, displayName: ct.displayName };
        });
        return cache;
    }, [coffeeTypes]);

    const handleGetLocation = async () => {
        try {
            setLocationError(null);
            setLocating(true);

            const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
                if (!navigator.geolocation) {
                    reject(new Error('Geolocation not supported'));
                    return;
                }
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 0
                });
            });

            setLocation({
                lat: pos.coords.latitude,
                lng: pos.coords.longitude
            });
        } catch (err: any) {
            setLocationError(err?.message || 'Failed to get location');
        } finally {
            setLocating(false);
        }
    };

    const handleSearch = () => {
        if (!selectedCoffeeType) {
            setLocationError('Please select a coffee type');
            return;
        }
        if (!location) {
            setLocationError('Please share your location first');
            return;
        }
        setLocationError(null);
        fetchRecommendations(location.lat, location.lng, selectedCoffeeType, transport);
    };

    return (
        <div className="space-y-6">
            {/* coffee type selector */}
            <div className="bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15 shadow-lg p-4">
                <h3 className="text-sm font-bold text-ink uppercase tracking-wide mb-3">
                    Pick your coffee
                </h3>
                {loadingTypes ? (
                    <div className="flex items-center justify-center py-4">
                        <div className="w-6 h-6 border-2 border-muted border-t-sky rounded-full animate-spin" />
                    </div>
                ) : (
                    <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                        {coffeeTypes.map(ct => (
                            <CoffeeBadge
                                key={ct.coffeeKey}
                                coffeeKey={ct.coffeeKey}
                                displayName={ct.displayName}
                                iconName={ct.iconName}
                                size="md"
                                selected={selectedCoffeeType === ct.coffeeKey}
                                onClick={() => setSelectedCoffeeType(ct.coffeeKey)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* location - prominent when not set */}
            {!location && (
                <div className="bg-sky/20 backdrop-blur-sm rounded-xl border-2 border-sky shadow-lg p-5">
                    <div className="text-center mb-4">
                        <h3 className="text-lg font-bold text-ink mb-1">
                            📍 Share your location
                        </h3>
                        <p className="text-sm text-ink/70">
                            We need your location to find coffee shops near you
                        </p>
                    </div>
                    <Button
                        onClick={handleGetLocation}
                        disabled={locating}
                        variant="default"
                        icon={faLocationCrosshairs}
                        mobileSlot="special2"
                        className="w-full text-base py-3"
                    >
                        {locating ? 'Getting location...' : 'Allow Location Access'}
                    </Button>
                    {locationError && (
                        <p className="text-sm text-pink font-medium text-center mt-3">
                            {locationError}
                        </p>
                    )}
                </div>
            )}

            {/* transport and search - shown after location is set */}
            {location && (
                <div className="bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15 shadow-lg p-4 space-y-4">
                    <p className="text-sm text-ink/70 text-center">
                        📍 {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                        <button 
                            onClick={handleGetLocation}
                            className="ml-2 text-sky underline hover:no-underline"
                            disabled={locating}
                        >
                            {locating ? 'updating...' : 'update'}
                        </button>
                    </p>
                    
                    <div>
                        <h3 className="text-sm font-bold text-ink uppercase tracking-wide mb-3">
                            How far can you go?
                        </h3>
                        <TransportPicker value={transport} onChange={setTransport} />
                    </div>

                    <Button
                        onClick={handleSearch}
                        disabled={loading}
                        variant="softPrimary"
                        icon={faSearch}
                        className="w-full"
                    >
                        {loading ? 'Searching...' : 'Search'}
                    </Button>

                    {error && (
                        <p className="text-sm text-pink font-medium text-center">
                            {error}
                        </p>
                    )}
                </div>
            )}

            {/* results */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                    <div className="w-10 h-10 border-4 border-muted border-t-sky rounded-full animate-spin mb-4" />
                    <p className="text-muted-foreground">Finding the best coffee near you...</p>
                </div>
            ) : recommendations.length === 0 ? (
                <div className="bg-paper/80 backdrop-blur-sm rounded-xl border border-ink/15 shadow-lg p-10 text-center">
                    <h3 className="text-xl font-bold text-ink mb-2">No recommendations yet</h3>
                    <p className="text-muted-foreground">
                        Choose a coffee type and share your location to see nearby suggestions.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                        {recommendations.length} recommendation{recommendations.length !== 1 ? 's' : ''} found
                        {selectedCoffeeType && coffeeTypesCache[selectedCoffeeType] && (
                            <span> for {coffeeTypesCache[selectedCoffeeType].displayName}</span>
                        )}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {recommendations.map((shop: any) => (
                            <ShopCard
                                key={shop._id}
                                shop={{
                                    ...shop,
                                    coffeeTypeStats: Object.fromEntries(
                                        (shop.topCoffeeTypes || []).map((tc: any) => [
                                            tc.type,
                                            { count: tc.count, averageRating: tc.averageRating, lastRated: '' }
                                        ])
                                    )
                                }}
                                coffeeTypesCache={coffeeTypesCache}
                                distanceKm={shop.distanceKm}
                                onClick={onShopClick ? () => onShopClick(shop._id) : undefined}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShopRecommendationsView;
